import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Trophy } from 'lucide-react';
import { resolveApiAssetUrl } from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { renderSettingText } from '../lib/textTemplate';
import {
  fetchSponsorships, sponsorshipTierLabel, sponsorshipTypeLabel,
} from '../lib/sponsorships';

/**
 * Sponsorluklar sayfası (`/sponsorluk`).
 *
 * Footer bandındaki kartların açıklamalı hâli. Sayfa **panelden yönetilen**
 * kayıtları çizer; kuruluma özel bir kulüp adı ya da metin koda gömülmez
 * (yazılım çok alıcıya satılıyor). Kayıt yoksa sayfa boş bir liste değil
 * açıklayıcı bir satır gösterir — footer bağlantısı her zaman görünür ve
 * tıklayan ziyaretçi bozuk bir sayfa görmemelidir.
 */
const Sponsorships = () => {
  const settings = useSettings();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchSponsorships().then((list) => {
      if (alive) setItems(list);
    });
    return () => { alive = false; };
  }, []);

  const brand = renderSettingText('{{site_name}}', settings);

  return (
    <main className="sponsorship-page">
      <header className="sponsorship-page-head">
        <h1>Sponsorluklarımız</h1>
        <p>
          {brand
            ? `${brand} olarak destek verdiğimiz kulüpler, ligler ve organizasyonlar.`
            : 'Destek verdiğimiz kulüpler, ligler ve organizasyonlar.'}
        </p>
      </header>

      {items === null && (
        <div className="flex justify-center py-10 text-slate-400">
          <Loader2 size={22} className="animate-spin" aria-label="Yükleniyor" />
        </div>
      )}

      {items !== null && items.length === 0 && (
        <p className="text-center text-sm text-slate-400 py-8">
          Şu anda yayınlanan bir sponsorluk bulunmuyor.
        </p>
      )}

      {items !== null && items.length > 0 && (
        <div className="sponsorship-page-list">
          {items.map((item) => {
            const accent = item.accent_color || 'var(--pusu-accent)';
            const meta = [sponsorshipTypeLabel(item), item.season]
              .filter(Boolean).join(' · ');
            return (
              <article
                key={item.id}
                className="sponsorship-detail"
                style={{ '--sponsorship-accent': accent }}
              >
                <div className="sponsorship-detail-logo">
                  {item.logo_url ? (
                    <img
                      src={resolveApiAssetUrl(item.logo_url)}
                      alt={item.name}
                      loading="lazy"
                    />
                  ) : (
                    <Trophy size={40} style={{ color: accent }} aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0">
                  <span
                    className="sponsorship-card-tier"
                    style={{ color: accent }}
                  >
                    {sponsorshipTierLabel(item)}
                  </span>
                  <h2>{item.name}</h2>
                  {meta && <span className="sponsorship-card-meta">{meta}</span>}
                  {item.description && (
                    <p className="sponsorship-detail-desc">{item.description}</p>
                  )}
                  {item.link && (
                    <a
                      className="sponsorship-detail-link"
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Resmi siteyi ziyaret et
                      <ExternalLink size={14} aria-hidden="true" />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
};

export default Sponsorships;
